import { appKey } from '@app/shared/brand'
import {
  createElement,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { Button } from '../ui'
import type { DonateAnswers } from './donate-answers'
import { recallAnswers, rememberAnswers } from './donate-answers'

export const isKioskMode = (search = window.location.search): boolean =>
  new URLSearchParams(search).has('kiosk')

export type GivingChoice =
  | 'one-time'
  | 'campaign'
  | 'membership'
  | 'recurring'
  | 'pledge'
  | 'installment'
  | 'account'

/** A shared-device flow offers only gifts it can take once and forget. */
const KIOSK_GIVING_CHOICES = new Set<GivingChoice>(['one-time', 'campaign'])

export const givingChoiceOffered = (
  choice: GivingChoice,
  kiosk: boolean,
): boolean => !kiosk || KIOSK_GIVING_CHOICES.has(choice)

export const KIOSK_DONE_MESSAGE = appKey('kiosk-done')
export const KIOSK_ACTIVE_MESSAGE = appKey('kiosk-active')
export const KIOSK_BUSY_MESSAGE = appKey('kiosk-busy')

export function kioskHref(href: string, kiosk: boolean): string {
  if (!kiosk) return href
  const separator = href.includes('?') ? '&' : '?'
  return `${href}${separator}kiosk`
}

export function recallDonateAnswers(
  orgId: string,
  kiosk: boolean,
): DonateAnswers | null {
  return kiosk ? null : recallAnswers(orgId)
}

export function rememberDonateAnswers(
  orgId: string,
  answers: DonateAnswers,
  kiosk: boolean,
): void {
  if (!kiosk) rememberAnswers(orgId, answers)
}

/** Tell the same-origin board that its disposable giving flow is finished. */
export function useKioskDone(kiosk: boolean, paid: boolean): () => void {
  const done = useCallback(() => {
    window.parent.postMessage({ type: KIOSK_DONE_MESSAGE }, location.origin)
  }, [])
  useEffect(() => {
    if (!kiosk || !paid) return
    const timer = window.setTimeout(done, 20_000)
    return () => window.clearTimeout(timer)
  }, [done, kiosk, paid])
  return done
}

/** Report card-field activity across Stripe's iframe, at most once a second. */
export function useKioskActivity(kiosk: boolean): () => void {
  const lastSent = useRef(0)
  return useCallback(() => {
    const now = Date.now()
    if (!kiosk || now - lastSent.current < 1000) return
    lastSent.current = now
    window.parent.postMessage({ type: KIOSK_ACTIVE_MESSAGE }, location.origin)
  }, [kiosk])
}

/** Hold the parent kiosk open while the bank is answering a payment. */
export function useKioskBusy(kiosk: boolean): {
  setBusy: (busy: boolean) => void
  release: () => void
} {
  const busy = useRef(false)
  const setBusy = useCallback(
    (next: boolean) => {
      if (!kiosk || busy.current === next) return
      busy.current = next
      window.parent.postMessage(
        { type: KIOSK_BUSY_MESSAGE, active: next },
        location.origin,
      )
    },
    [kiosk],
  )
  useEffect(
    () => () => {
      if (busy.current)
        window.parent.postMessage(
          { type: KIOSK_BUSY_MESSAGE, active: false },
          location.origin,
        )
    },
    [],
  )
  return { setBusy, release: () => setBusy(false) }
}

export function KioskDoneOr({
  kiosk,
  children,
}: {
  kiosk: boolean
  children: ReactNode
}) {
  const done = useKioskDone(kiosk, true)
  return kiosk
    ? createElement(Button, { variant: 'secondary', onClick: done }, 'Done')
    : children
}
